'use client';

import { Button, Grid, Modal, ModalFooter, ModalHeader } from '@coolblue-development/freddy';
import clickOnCompleteTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeInOverlay/clickOnCompleteTrigger';
import clickOnNextPreviousTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeInOverlay/clickOnNextPreviousTrigger';
import closeOverlayTrigger from '@coolblue-development/ga4-events/triggers/ProductTradeInOverlay/closeOverlayTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import {
  getNavigationButtonTrackingParams,
  getStepName
} from '@features/product/components/ProductTradeIn/tracking';
import type { SalesPrice } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { useContext, useEffect, useRef } from 'react';

import { getSummary } from '../actions';
import { ProductTradeInContext } from '../Context';
import { Actions } from '../types';
import { StyledModalBody } from './Modal.styled';
import Steps from './Steps';

function ProductTradeInModal({
  productTypeId,
  productType,
  salesPrice
}: {
  productTypeId: number;
  productType: string;
  salesPrice: SalesPrice;
}) {
  const {
    fetched,
    isModalOpen,
    isLoading,
    steps,
    currentStep = 0,
    goToStep,
    answers,
    selectedBrand,
    selectedVariant,
    summary,
    dispatch
  } = useContext(ProductTradeInContext);

  const logger = useClientLogger();
  const modalTranslations = useFreddyTranslations('modal');
  const { t } = useTranslation('webshop.productTradeIn');
  const { trackEvent } = useAnalytics();
  const { isMobile } = useBreakpoints();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch?.({ type: Actions.SET_PRODUCT_TYPE, payload: productType });
    dispatch?.({ type: Actions.SET_SALES_PRICE, payload: salesPrice.includingVat });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPreviousStepButtonDisabled = currentStep === 0 || isLoading;
  const isNextStepButtonDisabled = steps?.[currentStep]?.completed === false || isLoading;
  const nextStepName = 'next';
  const previousStepName = 'previous';

  function trackingEvent(currentStep: number, stepToSet: 'next' | 'previous') {
    trackEvent(
      clickOnNextPreviousTrigger(getNavigationButtonTrackingParams(currentStep, stepToSet))
    );
  }

  async function handleNextStepClick() {
    if (currentStep === 2 && !summary && selectedBrand && selectedVariant && answers) {
      dispatch?.({ type: Actions.SET_LOADING, payload: true });

      dispatch?.({
        type: Actions.SET_SUMMARY,
        payload: await getSummary({
          productTypeId,
          brandId: selectedBrand,
          optionId: Number(selectedVariant),
          answers
        })
      });

      dispatch?.({ type: Actions.SET_LOADING, payload: false });
    }

    trackingEvent(currentStep, nextStepName);
    goToStep?.(nextStepName);
  }

  function handlePreviousStepClick() {
    trackingEvent(currentStep, previousStepName);
    goToStep?.(previousStepName);
  }

  function handleCompleteClick() {
    const hasTradeInPrice = Boolean(summary?.tradeInPrice && summary?.tradeInPrice > 0);
    trackEvent(clickOnCompleteTrigger());
    dispatch?.({ type: Actions.SET_MODAL_OPEN, payload: false });
    dispatch?.({ type: Actions.SET_ENTRANCE_CHOICE, payload: hasTradeInPrice });
    logger.info('Product trade-in completed');
  }

  function handleCloseClick() {
    trackEvent(
      closeOverlayTrigger({
        sub_feature: getStepName(currentStep)
      })
    );
    dispatch?.({ type: Actions.SET_MODAL_OPEN, payload: false });

    if (!summary || summary?.tradeInPrice === 0) {
      dispatch?.({ type: Actions.SET_ENTRANCE_CHOICE, payload: false });
    }
  }

  const PreviousButton = currentStep > 0 && (
    <Button
      disabled={isPreviousStepButtonDisabled}
      type='button'
      variant='secondary'
      onClick={() => {
        handlePreviousStepClick();
        scrollRef.current?.scrollTo({ top: 0 });
      }}
    >
      {t('previousStepButtonLabel')}
    </Button>
  );

  const NextButton = currentStep < 3 && (
    <Button
      disabled={isNextStepButtonDisabled}
      type='button'
      onClick={() => {
        handleNextStepClick();
        scrollRef.current?.scrollTo({ top: 0 });
      }}
    >
      {t('continueButtonLabel')}
    </Button>
  );

  const CompleteButton = currentStep === 3 && (
    <Button type='button' onClick={handleCompleteClick} disabled={isLoading}>
      {t('completeButtonLabel')}
    </Button>
  );

  return (
    <>
      <Modal
        height={isMobile ? 620 : undefined}
        translations={modalTranslations}
        open={!!isModalOpen}
        onClose={handleCloseClick}
        size='xlarge'
        title={t('explanationModalTitle')}
        header={<ModalHeader title={t('explanationModalTitle')} />}
        placement={isMobile ? 'bottom' : 'center'}
        body={
          <StyledModalBody ref={scrollRef}>
            <Steps currentStep={currentStep} scrollRef={scrollRef} />
          </StyledModalBody>
        }
        footer={
          fetched && (
            <ModalFooter>
              {isMobile ? (
                <Grid
                  templateColumns={currentStep === 0 ? '1fr' : '1fr 1fr'}
                  sx={{ width: '100%' }}
                  gap={200}
                >
                  {PreviousButton}
                  {NextButton}
                  {CompleteButton}
                </Grid>
              ) : (
                <>
                  {PreviousButton}
                  {NextButton}
                  {CompleteButton}
                </>
              )}
            </ModalFooter>
          )
        }
      />
    </>
  );
}

export default ProductTradeInModal;
